import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Button,
  Card,
  Flex,
  Grid,
  Heading,
  Tabs,
  Text,
} from "@radix-ui/themes";
import { CreditCard, Link2 } from "lucide-react";
import InputField from "@/components/form/InputField";
import SelectField from "@/components/form/SelectField";
import { stripeConnectService } from "@/services/breeze/StripeConnectService";
import { CreateConnectAccountRequest } from "@/types/breeze/StripeConnect";

export default function StripeConnectSetup() {
  const queryClient = useQueryClient();

  const { mutate: createAccount, isPending: isCreatingAccount } = useMutation({
    mutationFn: (data: CreateConnectAccountRequest) =>
      stripeConnectService.createConnectAccount(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stripe-connect-account"] });
    },
  });

  return (
    <Tabs.Root defaultValue="create">
      <Tabs.List mb="4">
        <Tabs.Trigger value="create">
          <Flex align="center" gap="1">
            <CreditCard size={14} /> Create New Account
          </Flex>
        </Tabs.Trigger>
        <Tabs.Trigger value="connect">
          <Flex align="center" gap="1">
            <Link2 size={14} /> Connect Existing Account
          </Flex>
        </Tabs.Trigger>
      </Tabs.List>

      <Tabs.Content value="connect">
        <Card size="3">
          <Box p="2">
            <Heading as="h3" size="3" weight="bold" mb="2">
              Connect Your Existing Stripe Account
            </Heading>
            <Text size="2" color="gray" mb="4" style={{ display: "block" }}>
              Already have a Stripe account? Connect it to Anythink to start
              processing payments immediately. You'll be redirected to Stripe to
              authorise the connection.
            </Text>
            <Button
              size="3"
              type="button"
              onClick={async () => {
                const width = 600;
                const height = 800;
                const left = window.screen.width / 2 - width / 2;
                const top = window.screen.height / 2 - height / 2;

                const popup = window.open(
                  "",
                  "stripe-connect",
                  `width=${width},height=${height},top=${top},left=${left},resizable=yes,scrollbars=yes`
                );

                if (popup) {
                  popup.document.title = "Connecting Stripe...";
                  popup.document.body.innerHTML = `
                    <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #1f2937;">
                      <div style="border: 4px solid #f3f3f3; border-top: 4px solid #635BFF; border-radius: 50%; width: 40px; height: 40px; animation: spin 1s linear infinite; margin-bottom: 16px;"></div>
                      <p style="font-size: 16px; font-weight: 500; margin: 0;">Redirecting to Stripe...</p>
                      <style>
                        @keyframes spin {
                          0% { transform: rotate(0deg); }
                          100% { transform: rotate(360deg); }
                        }
                      </style>
                    </div>
                  `;
                }

                try {
                  const orgId = window.location.pathname.split("/")[2];
                  const redirectUri = `${window.location.origin}/org/${orgId}/auth/stripe-callback`;
                  const { url } =
                    await stripeConnectService.getOAuthUrl(redirectUri);
                  if (popup) {
                    popup.location.href = url;
                  }
                } catch (err) {
                  console.error("Failed to get OAuth URL:", err);
                  if (popup) {
                    popup.close();
                  }
                }
              }}
            >
              <Link2 size={16} /> Connect with Stripe
            </Button>
          </Box>
        </Card>
      </Tabs.Content>

      <Tabs.Content value="create">
        <Formik
          initialValues={{
            type: "express" as const,
            country: "GB",
            business_type: "company" as const,
            email: "",
            business_profile: { name: "", url: "", support_email: "" },
          }}
          validationSchema={Yup.object({
            country: Yup.string().required("Country is required"),
            business_type: Yup.string().required("Business type is required"),
            email: Yup.string().email("Invalid email").required("Email is required"),
            business_profile: Yup.object({
              name: Yup.string().required("Business name is required"),
            }),
          })}
          onSubmit={(values: CreateConnectAccountRequest) => createAccount(values)}
        >
          <Form>
            <Flex direction="column" gap="6">
              <Box>
                <Heading as="h3" size="3" weight="bold" mb="4">
                  Account Details
                </Heading>
                <Grid columns={{ initial: "1", md: "2" }} gap="4">
                  <SelectField name="type" label="Account Type" type="string" required>
                    <SelectField.Item value="express">Express (Recommended)</SelectField.Item>
                    <SelectField.Item value="standard">Standard</SelectField.Item>
                  </SelectField>
                  <SelectField name="country" label="Country" type="string" required>
                    <SelectField.Item value="GB">United Kingdom</SelectField.Item>
                  </SelectField>
                  <SelectField name="business_type" label="Business Type" type="string" required>
                    <SelectField.Item value="individual">Individual</SelectField.Item>
                    <SelectField.Item value="company">Company</SelectField.Item>
                    <SelectField.Item value="non_profit">Non-profit</SelectField.Item>
                    <SelectField.Item value="government_entity">Government</SelectField.Item>
                  </SelectField>
                  <InputField
                    name="email"
                    label="Email Address"
                    type="email"
                    required
                    options={{ placeholder: "business@example.com" }}
                  />
                </Grid>
              </Box>

              <Box>
                <Heading as="h3" size="3" weight="bold" mb="4">
                  Business Information
                </Heading>
                <Grid columns={{ initial: "1", md: "2" }} gap="4">
                  <InputField
                    name="business_profile.name"
                    label="Business Name"
                    required
                    options={{ placeholder: "Your Business Name" }}
                  />
                  <InputField
                    name="business_profile.url"
                    label="Website URL"
                    options={{ placeholder: "https://yourbusiness.com" }}
                  />
                </Grid>
              </Box>

              <Flex justify="start">
                <Button type="submit" loading={isCreatingAccount} size="3">
                  Create Stripe Connect Account
                </Button>
              </Flex>
            </Flex>
          </Form>
        </Formik>
      </Tabs.Content>
    </Tabs.Root>
  );
}
